<template>
  <form @submit.prevent="triggerCaptcha">
    <FormInput
      v-for="input in form"
      :key="input.name"
      v-model="model[input.name]"
      :disabled="checkConfig(input.disabled, isDone, disabled)"
      :readonly="checkConfig(input.readonly, isLoading, readonly)"
      v-bind="input"
    />
    <div
      id="recaptcha"
    />

    <StylizedSubmitButton
      :is-loading="isLoading"
      :disabled="checkConfig(isLoading || isDone, disabled)"
      class="mx-auto mt-7"
    >
      <template v-if="error">
        Error
        <div><small class="d-inline-block">Please try again later</small></div>
      </template>
      <template v-else-if="isDone">
        Thank you
        <div><small class="d-inline-block">We will reply you shortly</small></div>
      </template>
      <template v-else>
        {{ $t('sections.contact.form.button') }}
      </template>
    </StylizedSubmitButton>
  </form>
</template>

<script lang="ts">
import { PropType } from 'vue';
import StylizedSubmitButton from './StylizedSubmitButton.vue';
import FormInput from './FormInput.ts.vue';
import { FormInputProps } from './FormInput.types';
import { PartnerBaseComponent } from '../../common/PartnerBaseComponent';

const updateModel = <T extends any>(model: T): T => JSON.parse(JSON.stringify(model));

export default PartnerBaseComponent.extend({
  components: {
    StylizedSubmitButton,
    FormInput,
  },

  props: {
    value: {
      type: Object,
      required: true,
    },

    form: {
      type: Array as PropType<FormInputProps[]>,
      required: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    submitHandler: {
      type: Function as PropType<(data: any, captchaToken: string) => Promise<void>>,
      required: true,
    },
  },

  data() {
    return {
      model: updateModel(this.value),
      isLoading: false,
      error: null as null | Error,
      isDone: false,
      captchaToken: '',
    };
  },
  mounted() {
    const grecaptchaScript = document.createElement('script');
    grecaptchaScript.src = 'https://www.google.com/recaptcha/api.js';
    // access grecaptcha via window
    grecaptchaScript.onload = (): void => {
      this.initCaptcha();
    };
    document.head.appendChild(grecaptchaScript);
  },
  watch: {
    value(newValue): void {
      this.model = updateModel(newValue);
    },
  },

  methods: {
    initCaptcha(): void {
      setTimeout(
        () => {
          if (!window.grecaptcha.render) {
            this.initCaptcha();
          } else {
            // eslint-disable-next-line no-console
            console.log('render is available'); // TEMP
            const element = document.getElementById('recaptcha');

            window.grecaptcha.render(
              element,
              {
                sitekey: '6LcD3foUAAAAAMJDbHL5Bg5EEmnxGWEtgOs75uMo',
                size: 'invisible',
                callback: this.onCaptchaSuccess,
              },
            );
          }
        },
        100);
    },
    triggerCaptcha(): void {
      window.grecaptcha.execute();
    },
    async onCaptchaSuccess(token: string): Promise<void> {
      this.captchaToken = token;
      this.onSubmit();
    },
    async onSubmit(): Promise<void> {
      this.isLoading = true;
      this.error = null;
      this.isDone = false;

      try {
        await this.submitHandler(this.model, this.captchaToken);
        this.isDone = true;
      } catch (error) {
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    },

    checkConfig(...truths: (boolean | undefined)[]): boolean | undefined {
      return truths.find(truth => truth != null);
    },
  },
});
</script>
